import type { TomDoAviso } from '../components/shared/Aviso';

/**
 * O RELATO DO "REENVIAR LEMBRETE" (fase 0 do documento no padrão de Administrativo, Heitor 23/10/2026).
 *
 * As cinco telas que reenviam lembrete de assinatura (Ofícios, Portabilidade, Propostas, contrato digital do CRM e termos
 * aditivos) diziam "enviado" sem saber se foi. Medido no código em 23/10:
 *  - o backend contava TENTATIVAS (`notified`), não lembretes: o ClickSign recusava e a rota respondia `success: true`;
 *  - o contrato digital respondia `success: false` sem motivo, e a tela mostrava "Erro ao enviar lembrete" (lead 86, #55);
 *  - Portabilidade ignorava a resposta, e os Aditivos diziam "reenviado" mesmo com `success: false`.
 *
 * Esta função é o único lugar que traduz a resposta em frase, e lê os três formatos:
 *  1. o NOVO (contrato com o backend): `lembrados` e `naoLembrados` com nome e motivo;
 *  2. o de contagem (`notified`), sem nomes: diz o número, e com 0 diz que ninguém foi lembrado (nunca "sucesso");
 *  3. o de `success: false` com `message`: diz o motivo do servidor.
 * Resposta sem nada disso (Portabilidade antiga devolvia o objeto e a tela não lia) não vira "sucesso": diz que o pedido
 * foi aceito sem confirmação de quem recebeu.
 */
export interface NaoLembrado {
  nome: string;
  motivo: string;
}

export interface RespostaDoLembrete {
  success?: boolean;
  notified?: number;
  message?: string;
  lembrados?: string[];
  naoLembrados?: NaoLembrado[];
}

export interface RelatoDoLembrete {
  tom: TomDoAviso;
  texto: string;
}

const lista = (nomes: string[]): string =>
  nomes.length <= 1 ? (nomes[0] ?? '') : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;

export function relatoDoLembrete(r: RespostaDoLembrete | null | undefined): RelatoDoLembrete {
  const resposta = r || {};
  const lembrados = Array.isArray(resposta.lembrados) ? resposta.lembrados.filter(Boolean) : null;
  const naoLembrados = Array.isArray(resposta.naoLembrados) ? resposta.naoLembrados.filter((n) => n && n.nome) : [];

  // 1. Formato novo: nomes dos dois lados.
  if (lembrados) {
    const falhas = naoLembrados.map((n) => `${n.nome} (${n.motivo || 'sem motivo informado'})`);
    if (lembrados.length && !falhas.length) return { tom: 'success', texto: `Lembrete enviado a ${lista(lembrados)}.` };
    if (lembrados.length) return { tom: 'warning', texto: `Lembrete enviado a ${lista(lembrados)}. Não foi para ${lista(falhas)}.` };
    if (falhas.length) return { tom: 'error', texto: `Nenhum lembrete foi enviado. Não foi para ${lista(falhas)}.` };
    return { tom: 'warning', texto: 'Nenhum lembrete foi enviado: não há signatário pendente.' };
  }

  // 3. success:false com o motivo do servidor (o caso do contrato #55).
  if (resposta.success === false) {
    const motivo = (resposta.message || '').trim();
    return { tom: 'error', texto: motivo ? `O lembrete não foi enviado: ${motivo}.`.replace(/\.\.$/, '.') : 'O lembrete não foi enviado, e o servidor não disse por quê.' };
  }

  // 2. Só a contagem.
  if (typeof resposta.notified === 'number') {
    if (resposta.notified === 0) return { tom: 'warning', texto: 'Nenhum lembrete foi enviado: não há signatário pendente com link no ClickSign.' };
    return { tom: 'success', texto: `Lembrete pedido ao ClickSign para ${resposta.notified} signatário(s) pendente(s).` };
  }

  return { tom: 'info', texto: 'Pedido de lembrete aceito, sem confirmação de quem recebeu.' };
}

/** O relato de uma falha HTTP (a rota recusou: documento fora de "aguardando", sem permissão, rede). */
export function relatoDaFalhaDoLembrete(e: any): RelatoDoLembrete {
  const motivo = e?.response?.data?.message || e?.message || 'erro desconhecido';
  return { tom: 'error', texto: `O lembrete não foi enviado: ${Array.isArray(motivo) ? motivo.join('; ') : motivo}.`.replace(/\.\.$/, '.') };
}
